import React from 'react';

// Utility function to process bold text formatting
const processBoldText = (text: string): React.ReactNode => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  
  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          const boldText = part.slice(2, -2);
          return (
            <span key={`bold-${index}`} className="font-bold">
              {boldText}
            </span>
          );
        } else {
          return part || null;
        }
      })}
    </>
  );
};

/**
 * Formats message content with markdown-like formatting
 * Handles bullet points, line breaks, and basic formatting
 */
export function formatMessageContent(content: string): React.ReactNode {
  if (!content) return null;

  // Split content by double line breaks to create paragraphs
  const paragraphs = content.split('\n\n');

  return (
    <div className="space-y-3">
      {paragraphs.map((paragraph, paragraphIndex) => {
        // Check if this paragraph contains bullet points
        const lines = paragraph.split('\n');
        const hasBulletPoints = lines.some(line => line.trim().match(/^[*•-]\s+/));

        if (hasBulletPoints) {
          // Render as a list
          const listItems: React.ReactNode[] = [];
          let currentText = '';

          lines.forEach((line, lineIndex) => {
            const trimmedLine = line.trim();
            const bulletMatch = trimmedLine.match(/^[*•-]\s+(.+)$/);

            if (bulletMatch) {
              // If we have accumulated text before this bullet, add it as a paragraph
              if (currentText.trim()) {
                listItems.push(
                  <p key={`text-${lineIndex}`} className="mb-2 text-sm leading-relaxed break-all overflow-wrap-anywhere word-break-break-all overflow-hidden">
                    {currentText.trim()}
                  </p>
                );
                currentText = '';
              }

              // Add the bullet point
              listItems.push(
                <li key={`bullet-${lineIndex}`} className="text-sm leading-relaxed break-all overflow-wrap-anywhere word-break-break-all overflow-hidden">
                  {bulletMatch[1]}
                </li>
              );
            } else if (trimmedLine) {
              // Accumulate non-bullet text
              currentText += (currentText ? '\n' : '') + trimmedLine;
            }
          });

          // Add any remaining text
          if (currentText.trim()) {
            listItems.push(
              <p key="final-text" className="mb-2 text-sm leading-relaxed break-all overflow-wrap-anywhere word-break-break-all overflow-hidden">
                {currentText.trim()}
              </p>
            );
          }

          return (
            <div key={paragraphIndex}>
              {listItems.filter((item) => {
                // Separate paragraphs from list items
                return React.isValidElement(item) && item.type === 'p';
              })}

              {listItems.some(item => React.isValidElement(item) && item.type === 'li') && (
                <ul className="list-none space-y-1 ml-0">
                  {listItems.filter(item => React.isValidElement(item) && item.type === 'li')}
                </ul>
              )}
            </div>
          );
        } else {
          // Regular paragraph - preserve line breaks within
          const formattedLines = lines
            .filter(line => line.trim())
            .map((line, lineIndex) => (
              <React.Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {line.trim()}
              </React.Fragment>
            ));

          return (
            <p key={paragraphIndex} className="text-sm leading-relaxed break-all overflow-wrap-anywhere word-break-break-all overflow-hidden">
              {formattedLines}
            </p>
          );
        }
      })}
    </div>
  );
}

/**
 * Enhanced bullet point formatter with nested bullets and bold text support
 */
export function formatBulletPoints(content: string): React.ReactNode {
  // Split content into paragraphs
  return (
    <div className="space-y-4">
      {content.split('\n\n').map((paragraph, pIndex) => {
        const lines = paragraph.split('\n');
        return (
          <div key={`paragraph-${pIndex}`} className="space-y-2">
            {renderParagraphContent(lines, pIndex)}
          </div>
        );
      })}
    </div>
  );
}

// Render paragraph content with proper formatting
function renderParagraphContent(lines: string[], paragraphIndex: number): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Check if this line is a bullet point (including nested ones)
    const bulletMatch = line.match(/^(\s*)[*•-]\s+(.*)$/);

    if (bulletMatch) {
      // Collect all consecutive bullet points
      const listItems: React.ReactNode[] = [];
      
      while (i < lines.length) {
        const currentLine = lines[i];
        const currentBulletMatch = currentLine.match(/^(\s*)[*•-]\s+(.*)$/);

        if (currentBulletMatch) {
          const [, indent, content] = currentBulletMatch;
          const spaceCount = indent.length;
          const indentLevel = Math.floor(spaceCount / 4);

          listItems.push(
            <li
              key={`li-${paragraphIndex}-${i}`}
              className="text-sm leading-relaxed mb-1"
              style={{
                marginLeft: indentLevel > 0 ? `${indentLevel * 1.5}rem` : '0',
                listStyleType: indentLevel === 0 ? 'disc' : indentLevel === 1 ? 'circle' : 'square'
              }}
            >
              {processBoldText(content)}
            </li>
          );
          i++;
        } else {
          break;
        }
      }

      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${paragraphIndex}-${elements.length}`} className="pl-6">
            {listItems}
          </ul>
        );
      }
    } else {
      // Handle regular text lines
      if (trimmedLine) {
        // Preserve leading whitespace for indentation
        const leadingSpaces = line.match(/^(\s*)/)?.[1] || '';
        const indentStyle = leadingSpaces ? {
          paddingLeft: `${leadingSpaces.length * 0.5}rem`
        } : undefined;

        elements.push(
          <div
            key={`line-${paragraphIndex}-${i}`}
            className="text-sm leading-relaxed"
            style={indentStyle}
          >
            {processBoldText(trimmedLine)}
          </div>
        );
      } else {
        // Empty line for spacing
        elements.push(
          <div key={`empty-${paragraphIndex}-${i}`} className="h-2" />
        );
      }
      i++;
    }
  }

  return elements;
}